// ---------------------------------------------------------------------------
// Szenentext und Material (PLAN.md §36): Eine Szene hat genau einen Text, den Szenentext (Standarddokument „Draft“) – aus ihm kommen
// alle Analysen. Was man zur Szene sammelt (Ideen, Recherche, Links, Bilder), ist Material: kein Text der Geschichte, für keine Analyse.
// ---------------------------------------------------------------------------

import { draftDocOf } from './narrative.js';
import { isScene } from './scene.js';
import type { MaterialItem, Story, StoryNode } from './types.js';

export const materialOf = (node: Pick<StoryNode, 'material'>): MaterialItem[] => node.material ?? [];

/** Ein Link braucht eine Webadresse (http bzw. https) */
export const isWebUrl = (url: string): boolean => /^https?:\/\/[^\s/$.?#][^\s]*$/i.test(url.trim());

/** Eine eingegebene Adresse als Link: ohne Schema mit https:// davor; null, wenn es keine Webadresse ist */
export function webUrl(input: string): string | null {
  const s = input.trim();
  if (s === '') return null;
  const url = /^[a-z][a-z0-9+.-]*:/i.test(s) ? s : `https://${s}`;
  return isWebUrl(url) ? url : null;
}

/** Die Bilder, auf die das Material einer Szene verweist */
export const materialAssetIds = (node: Pick<StoryNode, 'material'>): string[] => materialOf(node).flatMap((m) => (m.kind === 'image' ? [m.assetId] : []));

/** Was ein Eintrag außer dem Titel trägt: der Text einer Idee bzw. die Adresse eines Links (ein Bild: nichts) */
export const materialBody = (m: MaterialItem): string => (m.kind === 'note' ? m.text : m.kind === 'link' ? m.url : '');

/** Text des Materials für die Suche: Titel, Notiz bzw. Adresse */
export const materialText = (m: MaterialItem): string => [m.title, materialBody(m)].filter((s) => s !== '').join('\n');

/**
 * Weitere Dokumente einer Szene (alles außer dem Szenentext) werden Material-Notizen – Name als Titel, Text unverändert –, hinter dem
 * schon vorhandenen Material. Liefert die geänderten Szenen (leer, wenn nichts zu tun ist).
 */
export function documentsToMaterial(story: Story, newId: () => string): StoryNode[] {
  const out: StoryNode[] = [];
  for (const n of story.nodes) {
    if (!isScene(n)) continue;
    const draft = draftDocOf(n);
    const extra = n.documents.filter((d) => d !== draft);
    if (extra.length === 0) continue;
    const notes = extra.map((d): MaterialItem => ({ id: newId(), kind: 'note', title: d.name, text: d.text }));
    out.push({ ...n, documents: draft ? [draft] : [], material: [...materialOf(n), ...notes] });
  }
  return out;
}
